import type { MarketResponseDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useOrderTimelineQuery } from "Order/Api/Query/useOrderTimelineQuery";
import { TimelineRecord } from "Order/Components/Detail/TimelineRecord";
import { deriveActivePartnerMap } from "Order/Utilities/deriveActivePartner";
import { isPartnerAllocationTimelineRecord } from "Order/Utilities/partnerAllocationHistory";
import { ExpandTimelineAnimation } from "Shared/Components/Animation/ExpandTimelineAnimation";
import { Rotate180degAnimation } from "Shared/Components/Animation/Rotate180deg";
import { BaseCard } from "Shared/Components/Cards/BaseCard";
import { ExpandMore } from "@mui/icons-material";
import { Timeline as MuiTimeline } from "@mui/lab";
import { Box } from "@mui/material";
import { type FC, useEffect, useMemo, useRef, useState } from "react";

const timelineResources = Resources.base.order.detail.timeline;

type OrderTimelineWidgetProps = {
	orderId: number;
	market: MarketResponseDto;
};

export const TIMELINE_MAX_HEIGHT = 400;

export const OrderTimelineWidget: FC<OrderTimelineWidgetProps> = ({
	orderId,
	market,
}) => {
	const [isExpanded, setIsExpanded] = useState<boolean>(false);
	const { t } = useResource();
	const { data } = useOrderTimelineQuery(orderId);
	const timelineRef = useRef<HTMLDivElement>(null);
	const [contentHeight, setContentHeight] = useState<number>(0);

	useEffect(() => {
		if (timelineRef.current) {
			setContentHeight(timelineRef.current.scrollHeight);
		}
	}, [data]);

	// Derive which partner was active at each timeline entry point
	const activePartnerMap = useMemo(
		() => (data ? deriveActivePartnerMap(data) : new Map()),
		[data],
	);

	const timelineRecords = data?.filter(
		(item) => !isPartnerAllocationTimelineRecord(item),
	);

	return (
		<BaseCard
			title={t(timelineResources.title)}
			sx={{ position: "relative", overflow: "hidden" }}
		>
			<ExpandTimelineAnimation
				isExpanded={isExpanded}
				componentRef={timelineRef}
				contentHeight={contentHeight}
				setContentHeight={setContentHeight}
			>
				<div ref={timelineRef}>
					<MuiTimeline sx={{ padding: 0 }}>
						{timelineRecords?.map((timelineRecord, index) => (
							<TimelineRecord
								key={`${index}${timelineRecord.key}`}
								item={timelineRecord}
								isLastItem={index === timelineRecords.length - 1}
								market={market}
								activePartnerInfo={activePartnerMap.get(timelineRecord.id)}
							/>
						))}
					</MuiTimeline>
				</div>
			</ExpandTimelineAnimation>

			{contentHeight > TIMELINE_MAX_HEIGHT ? (
				<Box
					position="absolute"
					bottom={0}
					left={0}
					right={0}
					bgcolor="white"
					display="flex"
					justifyContent="center"
					p={1}
					sx={{
						backdropFilter: "blur(3px)",
						background:
							"linear-gradient(to top, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0))",
						boxShadow: "0px -6px 12px rgba(0, 0, 0, 0.05)",
						cursor: "pointer",
					}}
					onClick={() => setIsExpanded((prev) => !prev)}
				>
					<Rotate180degAnimation hasChanged={isExpanded}>
						<ExpandMore fontSize="large" />
					</Rotate180degAnimation>
				</Box>
			) : (
				<></>
			)}
		</BaseCard>
	);
};
